import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ContentSection } from './contentsection';
import { selfEmployedInvoicingContentSectionsPart1, selfEmployedInvoicingContentSectionsPart2, selfEmployedInvoicingContentSectionsPart3, selfEmployedInvoicingContentSectionsPart4, selfEmployedInvoicingContentSectionsPart5, selfEmployedInvoicingContentSectionsPart6, selfEmployedInvoicingContentSectionsPart7, selfEmployedInvoicingContentSectionsPart8 } from '../constant/self-employed-invoicing-sections.constant';
import '../scss/self-employed-invoicing.style.scss';
import { selfEmployedImage1, selfEmployedImage2, selfEmployedImage3, selfEmployedImage4, selfEmployedImage5, businessRegistrationIcon, timerIcon, fileIcon, walletIcon, selfEmployedImage6 } from './images/jpg/selfEmployedInvoicingImage';
import { LOCALES } from '../../common/constants';

const SelfEmployedInvoicingPageComponent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const language = localStorage.getItem("i18nextLng");

    const handleReadMoreClick = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate( language === LOCALES.EN_US ? `/self-employment?lang=${LOCALES.EN_US}` : `/egenanstallning?lang=${LOCALES.SV_SE}`);
    };

    const handleFTaxGuideClick = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate(language === LOCALES.EN_US ? `/invoice-without-company-or-business?lang=${LOCALES.EN_US}` : `/fakturera-utan-foretag-eller-bolag?lang=${LOCALES.SV_SE}`);
    };

    return (
        <div className="self-employed-invoicing-page">
            {/* Main Content Section */}
            <section className="main-content-section">
                <Container className='px-0'>
                    <Row className="content-row">
                        <Col lg={6} className="content-left">
                            <h2 className="content-title">
                                {t("INVOICE_AS_PRIVATE_INDIVIDUAL_AND_SELF_EMPLOYED")}
                            </h2>
                        </Col>
                        <Col lg={6} className="content-right">
                            <div className="content-description">
                                <p>{t("SELF_EMPLOYED_INVOICING_HERO_DESCRIPTION_PART_1")}</p>
                                <p>{t("SELF_EMPLOYED_INVOICING_HERO_DESCRIPTION_PART_2")}</p>
                                <div className="read-more-link">
                                    <a href="#" className="link-button" onClick={handleReadMoreClick}>
                                        {t("READ_MORE_ABOUT_HOW_YOU_CAN_BECOME_SELF_EMPLOYED")}
                                        <span className="arrow-icon">→</span>
                                    </a>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            {/* First Image Section */}
            <section className="image-section">
                <Container className='px-0'>
                    <Row>
                        <Col>
                            <img src={selfEmployedImage1} alt="Self Employed" className="section-image" />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - Growing Number of Freelancers */}
            <section className="content-text-section freelancers-bg">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("THE_NUMBER_OF_SELF_EMPLOYED_FREELANCERS_IS_GROWING")}</h3>
                            <p className="section-text">
                                {t("THE_NUMBER_OF_SELF_EMPLOYED_FREELANCERS_IS_GROWING_TEXT")}
                            </p>
                        </Col>
                        <Col lg={6} className="content-right">
                            <div className="image-container">
                                <img src={selfEmployedImage2} alt={t("THE_NUMBER_OF_SELF_EMPLOYED_FREELANCERS_IS_GROWING")} className="content-image" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - Legal Regulation */}
            <section className="content-text-section legal-bg">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <div className="image-container">
                                <img src={selfEmployedImage3} alt={t("LEGALITY_AND_REGULATION_FOR_SELF_EMPLOYED_PRIVATE_INDIVIDUALS")} className="content-image" />
                            </div>
                        </Col>
                        <Col lg={6} className="content-right">
                            <h3 className="section-title">{t("LEGALITY_AND_REGULATION_FOR_SELF_EMPLOYED_PRIVATE_INDIVIDUALS")}</h3>
                            <p className="section-text">
                                {t("LEGALITY_AND_REGULATION_FOR_SELF_EMPLOYED_PRIVATE_INDIVIDUALS_TEXT")}
                            </p>
                            <div className="link-section">
                                <a href="#" className="link-button" onClick={handleFTaxGuideClick}>
                                    {t("INVOZIO_GUIDE_ON_F_TAX_AND_INVOICING_WITHOUT_COMPANY")}
                                    <span className="arrow-icon">→</span>
                                </a>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - What Does it Mean */}
            <section className="content-text-section meaning-bg">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("WHAT_DOES_IT_MEAN_TO_INVOICE_AS_PRIVATE_INDIVIDUAL")}</h3>
                            <p className="section-text">
                                {t("WHAT_DOES_IT_MEAN_TO_INVOICE_AS_PRIVATE_INDIVIDUAL_TEXT_1")}
                            </p>
                            <p className="section-text">
                                {t("WHAT_DOES_IT_MEAN_TO_INVOICE_AS_PRIVATE_INDIVIDUAL_TEXT_2")}
                            </p>
                        </Col>
                        <Col lg={6} className="content-right">
                            <div className="image-container">
                                <img src={selfEmployedImage4} alt={t("WHAT_DOES_IT_MEAN_TO_INVOICE_AS_PRIVATE_INDIVIDUAL")} className="content-image" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - What is Invozio */}
            <section className="content-text-section invozio-section">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("WHAT_IS_INVOZIO_AND_HOW_DOES_IT_WORK")}</h3>
                        </Col>
                        <Col lg={6} className="content-right">
                            <p className="section-text">
                                {t("WHAT_IS_INVOZIO_AND_HOW_DOES_IT_WORK_TEXT")}
                            </p>
                            <div className="link-section">
                                <a href="#" className="link-button">
                                    {t("INVOZIO_SALARY_GUIDE")}
                                    <span className="arrow-icon">→</span>
                                </a>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Second Image Section */}
            <section className="image-section">
                <Container className='px-0'>
                    <Row >
                        <Col>
                            <img src={selfEmployedImage5} alt="How Invozio Works" className="section-image" />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - Why Choose */}
            <section className="content-text-section why-choose-section">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("WHY_CHOOSE_TO_INVOICE_WITHOUT_COMPANY")}</h3>
                        </Col>
                        <Col lg={6} className="content-right">
                            <p className="section-text">
                                {t("WHY_CHOOSE_TO_INVOICE_WITHOUT_COMPANY_TEXT")}
                            </p>
                            <div className="link-section">
                                <a href="#" className="link-button">
                                    {t("LEARN_MORE_ABOUT_HOW_INVOZIO_WORKS")}
                                    <span className="arrow-icon">→</span>
                                </a>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - Advantages */}
            <section className="content-text-section advantages-section">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("THE_ADVANTAGES_OF_INVOICING_AS_PRIVATE_INDIVIDUAL")}</h3>
                        </Col>
                        <Col lg={6} className="content-right">
                            <p className="section-text">
                                {t("THE_ADVANTAGES_OF_INVOICING_AS_PRIVATE_INDIVIDUAL_TEXT")}
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Benefits Section with 4 Cards */}
            <section className="benefits-section">
                <Container className='px-0'>
                    <Row className="benefits-grid">
                        <Col lg={3} md={6} sm={12} className="benefit-item">
                            <div className="benefit-card">
                                <div className="benefit-header">
                                    <div className="benefit-icon">
                                        <img src={businessRegistrationIcon} alt={t("NO_BUSINESS_REGISTRATION_REQUIRED")} />
                                    </div>
                                    <h5 className="benefit-title">{t("NO_BUSINESS_REGISTRATION_REQUIRED")}</h5>
                                </div>
                                <p className="benefit-description">
                                    {t("NO_BUSINESS_REGISTRATION_REQUIRED_DESC")}
                                </p>
                            </div>
                        </Col>
                        <Col lg={3} md={6} sm={12} className="benefit-item">
                            <div className="benefit-card">
                                <div className="benefit-header">
                                    <div className="benefit-icon">
                                        <img src={timerIcon} alt={t("QUICK_AND_EASY_PROCESS")} />
                                    </div>
                                    <h5 className="benefit-title">{t("QUICK_AND_EASY_PROCESS")}</h5>
                                </div>
                                <p className="benefit-description">
                                    {t("QUICK_AND_EASY_PROCESS_DESC")}
                                </p>
                            </div>
                        </Col>
                        <Col lg={3} md={6} sm={12} className="benefit-item">
                            <div className="benefit-card">
                                <div className="benefit-header">
                                    <div className="benefit-icon">
                                        <img src={fileIcon} alt={t("AUTOMATIC_TAX_MANAGEMENT")} />
                                    </div>
                                    <h5 className="benefit-title">{t("AUTOMATIC_TAX_MANAGEMENT")}</h5>
                                </div>
                                <p className="benefit-description">
                                    {t("AUTOMATIC_TAX_MANAGEMENT_DESC")}
                                </p>
                            </div>
                        </Col>
                        <Col lg={3} md={6} sm={12} className="benefit-item">
                            <div className="benefit-card">
                                <div className="benefit-header">
                                    <div className="benefit-icon">
                                        <img src={walletIcon} alt={t("PENSIONABLE_INCOME")} />
                                    </div>
                                    <h5 className="benefit-title">{t("PENSIONABLE_INCOME")}</h5>
                                </div>
                                <p className="benefit-description">
                                    {t("PENSIONABLE_INCOME_DESC")}
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - Risks */}
            <section className="content-text-section risks-section">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("RISKS_OF_SELF_EMPLOYMENT_AND_HOW_INVOZIO_HELPS")}</h3>
                        </Col>
                        <Col lg={6} className="content-right">
                            <p className="section-text">
                                {t("RISKS_OF_SELF_EMPLOYMENT_AND_HOW_INVOZIO_HELPS_TEXT")}
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Third Image Section */}
            <section className="image-section">
                <Container className='px-0'>
                    <Row >
                        <Col>
                            <img src={selfEmployedImage6} alt="Risks and How Invozio Helps" className="section-image" />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Content Section - How Does Invoicing Work */}
            <section className="content-text-section invoicing-work-section">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("HOW_DOES_INVOICING_WORK_FOR_PRIVATE_INDIVIDUALS")}</h3>
                        </Col>
                        <Col lg={6} className="content-right">
                            <p className="section-text">
                                {t("HOW_DOES_INVOICING_WORK_FOR_PRIVATE_INDIVIDUALS_TEXT")}
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Process Steps Section */}
            <section className="process-steps-section">
                <Container className='px-0'>
                    <div className="steps-container">
                        <div className="step-item">
                            <div className="step-circle">
                                <span className="step-number">1</span>
                            </div>
                            <h5 className="step-title">{t("REGISTER")}</h5>
                            <p className="step-description">{t("REGISTER_ON_INVOZIO_PLATFORM")}</p>
                        </div>
                        <div className="step-item">
                            <div className="step-circle">
                                <span className="step-number">2</span>
                            </div>
                            <h5 className="step-title">{t("CREATE_PROFILE")}</h5>
                            <p className="step-description">{t("CREATE_PROFILE_WITH_PERSONAL_INFO")}</p>
                        </div>
                        <div className="step-item">
                            <div className="step-circle">
                                <span className="step-number">3</span>
                            </div>
                            <h5 className="step-title">{t("ADD_DATA")}</h5>
                            <p className="step-description">{t("ADD_CUSTOMER_DETAILS_AND_SPECIFY_WORK")}</p>
                        </div>
                        <div className="step-item">
                            <div className="step-circle">
                                <span className="step-number">4</span>
                            </div>
                            <h5 className="step-title">{t("SEND_INVOICE")}</h5>
                            <p className="step-description">{t("SEND_INVOICE_THROUGH_PLATFORM")}</p>
                        </div>
                        <div className="step-item">
                            <div className="step-circle">
                                <span className="step-number">5</span>
                            </div>
                            <h5 className="step-title">{t("RECEIVE_SALARY")}</h5>
                            <p className="step-description">{t("RECEIVE_SALARY_AFTER_TAXES_AND_FEES")}</p>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Content Section - Taxes and Fees */}
            <section className="content-text-section taxes-section">
                <Container className='px-0'>
                    <Row>
                        <Col lg={6} className="content-left">
                            <h3 className="section-title">{t("TAXES_AND_FEES_FOR_SELF_EMPLOYED")}</h3>
                        </Col>
                        <Col lg={6} className="content-right">
                            <p className="section-text">
                                {t("TAXES_AND_FEES_FOR_SELF_EMPLOYED_TEXT")}
                                <a href="https://verksamt.se/personal-rekrytering/vad-kostar-det/redovisa-och-betala-arbetsgivaravgifter" target="_blank" rel="noopener noreferrer" className="inline-link">
                                    Verksamt.se
                                </a>
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default SelfEmployedInvoicingPageComponent;
